import React, { useEffect, useMemo, useState } from "react";
import { FormControlLabel, Stack, Switch } from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import PasswordField from "../../../../components/common/PasswordField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import {
  email,
  matchField,
  minLength,
  mobile,
  password,
  required,
  sanitizeNationalPhone,
} from "../../../../utils/formValidation";

const createSchema = {
  firstname: [required("First name is required.")],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  password: [required("Password is required."), minLength(8), password({ min: 8 })],
  confirmPassword: [
    required("Confirm password is required."),
    matchField("password", "Passwords do not match."),
  ],
};

const editSchema = {
  firstname: [required("First name is required.")],
  lastname: [],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  mobile: [mobile()],
};

const emptyCreateValues = {
  firstname: "",
  username: "",
  email: "",
  password: "",
  confirmPassword: "",
};

const emptyEditValues = {
  firstname: "",
  lastname: "",
  username: "",
  email: "",
  mobile: "",
};

const SystemUserFormDialog = ({
  open,
  mode = "create",
  user = null,
  roles = [],
  rolesLoading = false,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const schema = isCreate ? createSchema : editSchema;
  const [formError, setFormError] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [enabled, setEnabled] = useState(true);

  const initialValues = isCreate ? emptyCreateValues : emptyEditValues;
  const { values, getFieldProps, validateAll, reset, setFieldValue } =
    useFormValidation(initialValues, schema);

  useEffect(() => {
    if (!open) return;

    setFormError("");
    if (isCreate) {
      reset(emptyCreateValues);
      setSelectedRoles([]);
      setEnabled(true);
      return;
    }

    reset({
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      username: user?.username || "",
      email: user?.email || "",
      mobile: sanitizeNationalPhone(user?.mobile || ""),
    });
    setSelectedRoles(Array.isArray(user?.roles) ? [...user.roles] : []);
    setEnabled(user?.enabled !== false);
  }, [isCreate, open, reset, user]);

  const roleOptions = useMemo(() => {
    const fromApi = (roles || []).map((role) =>
      typeof role === "string" ? role : role?.name || role?.role || String(role)
    );
    return Array.from(new Set([...fromApi, ...selectedRoles].filter(Boolean)));
  }, [roles, selectedRoles]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(
        errors.firstname ||
          errors.username ||
          errors.email ||
          errors.password ||
          errors.confirmPassword ||
          errors.mobile ||
          "Please correct the highlighted fields."
      );
      return;
    }

    if (isCreate && !selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    try {
      if (isCreate) {
        await onSubmit({
          firstname: values.firstname.trim(),
          username: values.username.trim(),
          email: values.email.trim(),
          password: values.password,
          roles: selectedRoles,
        });
      } else {
        await onSubmit({
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          username: values.username.trim(),
          email: values.email.trim(),
          mobile: sanitizeNationalPhone(values.mobile),
          enabled,
        });
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save user.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add System User" : "Edit System User"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create User" : "Save Changes"}
      loadingLabel="Saving..."
    >
      {isCreate ? (
        <FormField label="First Name" required {...getFieldProps("firstname")} />
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
          <FormField label="First Name" required {...getFieldProps("firstname")} />
          <FormField label="Last Name" {...getFieldProps("lastname")} />
        </Stack>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
        <FormField label="Username" required {...getFieldProps("username")} />
        <FormField label="Email" type="email" required {...getFieldProps("email")} />
      </Stack>

      {isCreate ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
            <PasswordField label="Password" required {...getFieldProps("password")} />
            <PasswordField
              label="Confirm Password"
              required
              {...getFieldProps("confirmPassword")}
            />
          </Stack>
          <ChipAutocomplete
            label="Roles"
            required
            freeSolo
            options={roleOptions}
            value={selectedRoles}
            onChange={setSelectedRoles}
            loading={rolesLoading}
            disabled={loading}
            helperText="At least one role is required. Example: idpadmin"
          />
        </>
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <FormField
            label="Mobile"
            {...getFieldProps("mobile")}
            onChange={(event) => {
              setFieldValue("mobile", sanitizeNationalPhone(event.target.value));
            }}
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
          />
          <FormControlLabel
            sx={{ ml: 0, minHeight: 40 }}
            control={
              <Switch
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
                disabled={loading}
              />
            }
            label={enabled ? "Enabled" : "Disabled"}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default SystemUserFormDialog;
